import { useCallback, useMemo } from 'react';

import axios from 'axios';
import useAxios from 'axios-hooks';
import { useLocation, useParams } from 'react-router-dom';

import strings, { url } from 'constants/index';
import { EnumValueNames, useEnum } from 'contexts/EnumValuesContext';
import { buildRequestParams } from 'utils/pagination.utils';

import { PermissionForm } from './components/PermissionsForm';
import { PermissionItem, PermissionsModel } from './Permissions.types';

const updateResources = (input: Array<PermissionItem> = []) =>
  input.map(data => {
    const newData = { ...data };
    newData.resourceValue =
      typeof newData.resourceValue === 'string'
        ? newData.resourceValue
        : newData.resourceValue.id.toString();
    newData.childPermissions = updateResources(data.childPermissions || []);
    return newData;
  });

export const PermissionsEditor = () => {
  //@todo If this is a component, can be passed as a prop
  const { id: userId } = useParams<string>();
  const { pathname } = useLocation();
  const isIngestionSourcesRoute = pathname.startsWith('/settings/ingestion-sources');

  const resourcetype = useEnum(EnumValueNames.Resource);

  const [{ data }, refetch] = useAxios<PaginationData<PermissionsModel>>({
    url: url.user_access,
    params: buildRequestParams({
      filters: [{ columnId: 'id', value: userId }],
    }),
  });

  const updateChildPermissions = useCallback((permission: PermissionItem): any => {
    const { resourceType, resourceValue } = permission;
    if (permission?.childPermissions.length > 0) {
      return {
        ...permission,
        childPermissions: permission.childPermissions.flatMap(updateChildPermissions),
        parentResourceType: resourceType,
        parentResourceValue: resourceValue,
      };
    }
    return {
      ...permission,
      parentResourceType: resourceType,
      parentResourceValue: resourceValue,
    };
  }, []);
  const modified = useMemo<PermissionsModel>(() => {
    const userAccess = data?.items[0];
    let user: PermissionsModel = {
      ...userAccess,
      permissions:
        userAccess?.permissions.length > 0
          ? userAccess.permissions.map(updateChildPermissions)
          : [],
    };
    if (!userAccess) {
      user = undefined;
    }
    return resourcetype.length && user
      ? {
          ...user,
          permissions: updateResources(user?.permissions),
        }
      : ({} as PermissionsModel);
  }, [data, resourcetype.length, updateChildPermissions]);
  const onFormSubmit = useCallback(
    async (formData: PermissionsModel) => {
      const values = {
        id: formData.id,
        isAdmin: formData.isAdmin,
        isReadAdmin: formData.isReadAdmin,
        ...(isIngestionSourcesRoute && {
          ingestionDisabled: formData.ingestionDisabled,
          ingestionDisabledCompetitions: formData.ingestionDisabledCompetitions,
        }),
        minimumDifferenceBetweenFixtures: formData.minimumDifferenceBetweenFixtures,
        matchingFixturesRange: formData.matchingFixturesRange,
        permissions: updateResources(formData.permissions),
        priority: formData.priority,
      };
      await axios.put(url.user_access, values);
      await refetch();
    },
    [isIngestionSourcesRoute, refetch],
  );
  if (Object.keys(modified).length === 0) {
    return <div>{strings.no_user_found}</div>; //shouldn't happen, but better be sure.
  }

  return (
    <PermissionForm
      data={modified}
      onFormSubmit={onFormSubmit}
      showIngestionSettings={isIngestionSourcesRoute}
    />
  );
};
